import React from 'react';
import { useParams } from 'react-router-dom';
import { isValid as reduxFormIsValid } from 'redux-form';
import { connect, MapStateToPropsParam } from 'react-redux';
import T from 'components/T';
import { i18n } from '@international/mastodon-i18n';
import { normalizeVerificationId } from '../../../api/verifications/verificationCache';
import { VerificationSubmitError } from '../../../api/verifications/submitError';
import { datadogLogs } from '@datadog/browser-logs';
import { M } from '@dashboard-experience/mastodon';
import useAuthGateState from '../../../hooks/useAuthGateState';
import useBreakpoint from '../../../hooks/useBreakpoint';
import {
  useVerificationActions,
  useVerificationValue,
} from '../../../providers/VerificationProvider';
import MainContainer from '../../MainContainer';
import { trackAnalyticsEvent as trackAnalyticsEventAction } from '../../../actions';
import { ADA_CONTROL_ID, VERIFICATION_FORM_NAME } from '../../../constants';
import { VerificationWrapper } from './VerificationWrapper';
import TranslationsObject from '../../../lib/translations/en.json';
import VerificationHeader from './VerificationHeader';

export interface VerificationProps {
  currentPage: number;
  expired: boolean;
  isSmallOrDown: boolean;
  isLogged: boolean;
  isValid: boolean;
  onSubmit: CallableFunction;
  steps: any[];
  submitting: boolean;
  verification: Record<string, any>;
  verificationError?: any;
  handleNextPage: (currentPage: number) => void;
  handlePreviousPage: (currentPage: number) => void;
  trackAnalyticsEvent: CallableFunction;
  showNav?: boolean;
}

interface VerificationState {
  showNav: boolean;
}

// The positional `values` fill `{{var}}`/`{{var2}}` placeholders, which only
// `i18n.getStr()` interpolates (`<T>` handles `%{name}` tokens). `Verification`
// is a class component with no locale subscription, so the lookup lives in its
// own `renderTranslation()`-wrapped component to re-render on a language switch.
const SubmitErrorWithValues = i18n.renderTranslation()(
  ({ error }: { error: VerificationSubmitError }) => (
    <>{i18n.getStr(error.translationKey, error.values)}</>
  ),
);

const errorMessage = (verificationError: any) => {
  // A failure the submit thunks already turned into a translation key (no
  // response, or a rejected file size).
  if (verificationError instanceof VerificationSubmitError) {
    return verificationError.values ? (
      <SubmitErrorWithValues error={verificationError} />
    ) : (
      <T value={verificationError.translationKey} dangerousHTML />
    );
  }
  if (
    typeof verificationError === 'string' &&
    Object.prototype.hasOwnProperty.call(TranslationsObject, verificationError)
  ) {
    return <T value={verificationError} />;
  }
  if (typeof verificationError === 'string') {
    return <>{verificationError}</>;
  }
  if (typeof verificationError?.message === 'string') {
    return <>{verificationError.message}</>;
  }
  return (
    <T value='components.Verification.DocumentsPanel.generic' dangerousHTML />
  );
};

class Verification extends React.Component<
  VerificationProps,
  VerificationState
> {
  constructor(props: VerificationProps) {
    super(props);

    this.wizardOnSubmit = this.wizardOnSubmit.bind(this);

    this.state = {
      showNav: props.showNav ?? true,
    };
  }

  getRenderAttributes() {
    const { showNav } = this.state;
    const {
      currentPage,
      steps,
      expired,
      isValid,
      isSmallOrDown,
      onSubmit,
      submitting,
      verification,
      handleNextPage,
      handlePreviousPage,
      isLogged,
      trackAnalyticsEvent,
    } = this.props;

    const canceled = !!verification.canceledAt;
    const completed = !!verification.completedAt;

    const firstStep = currentPage === 0;
    const isFinalStep = currentPage === steps.length - 1;

    const doc = steps[currentPage];
    const needUpload = doc?.input === 'upload';

    const showPreviousBtn = isLogged ? currentPage > 1 : currentPage > 0;
    const showNextStepBtn = !needUpload || (needUpload && isValid);

    return {
      showFirstPage: !isLogged,
      completed,
      currentPage,
      doc,
      firstStep,
      showNextStepBtn,
      showPreviousBtn,
      steps,
      expired,
      canceled,
      isFinalStep,
      isSmallOrDown,
      isValid,
      onSubmit,
      needUpload,
      submitting,
      verification,
      handleNextPage: handleNextPage.bind(this, currentPage),
      handlePreviousPage: handlePreviousPage.bind(this, currentPage),
      trackAnalyticsEvent,
      showNav,
    };
  }

  wizardOnSubmit(data: any) {
    const { isFinalStep, onSubmit, handleNextPage } =
      this.getRenderAttributes();

    if (isFinalStep) {
      onSubmit(data);
    } else {
      handleNextPage();
    }
  }

  renderCustomPanel(): React.ReactNode {
    return null;
  }

  componentDidMount() {
    const existingScript = document.getElementById(ADA_CONTROL_ID);
    if (existingScript) {
      existingScript.remove();
    }

    const { verification } = this.props;
    datadogLogs.logger.info('[CP] Verification component mounted', {
      object: verification.object,
    });
  }

  render() {
    const { verification, steps, isLogged, verificationError, currentPage } =
      this.props;
    const attributes = this.getRenderAttributes();
    const isWelcomePage = Object.keys(steps[currentPage]).length === 0;

    return (
      <MainContainer
        data-testid='verification-page'
        className={'verification__wrapper verification__wrapper--new-flow'}
        header={
          attributes.completed ||
          (!isWelcomePage && verification.object !== 'matrix_verification') ? (
            <VerificationHeader
              companyName={verification.company}
              showLogin={Boolean(
                (verification.object !== 'ssn_check_failure' ||
                  steps[1].model === 'matrix_verification') &&
                  verification.completedAt,
              )}
              isLogged={isLogged}
            />
          ) : null
        }
      >
        {verificationError && (
          // ActionableNotification, not InlineNotification: errorMessage()'s
          // generic fallback contains a help-center link, and Carbon's
          // InlineNotification rejects interactive children. `inline` + the
          // maxWidth override keep it full-width in the page flow (not a
          // toast); closeOnEscape/hasFocus off so the error can't be
          // dismissed while verificationError is still set.
          <M.ActionableNotification
            kind='error'
            title=''
            subtitle={errorMessage(verificationError)}
            hideCloseButton
            inline
            closeOnEscape={false}
            hasFocus={false}
            style={{ maxWidth: '100%' }}
          />
        )}
        <VerificationWrapper
          {...attributes}
          wizardOnSubmit={this.wizardOnSubmit}
        >
          {this.renderCustomPanel()}
        </VerificationWrapper>
      </MainContainer>
    );
  }
}

// portalAuth.loggedIn comes from useAuthGateState (the AuthProvider Context)
// via the `authLoggedIn` prop below, not from the Redux portal slice. No default on
// the ownProps parameter: react-redux only passes ownProps when the function
// has two declared parameters (`length === 2`), and a default drops the count.
// The verification itself (`steps`, `submitting`, `verification`, `expired`)
// and the wizard step (`currentPage`) come from the VerificationProvider,
// through `steps` and `currentPage` here and the others passed to the component
// by the wrapper below.
export const mapStateToProps: MapStateToPropsParam<any, any, any> = (
  state: any,
  {
    authLoggedIn,
    steps,
    currentPage: page,
  }: { authLoggedIn?: boolean; steps: any[]; currentPage: number },
) => {
  const isLogged = authLoggedIn || false;
  const currentPage = !isLogged || page > 0 ? page : 1;

  return {
    isLogged,
    isValid: reduxFormIsValid(VERIFICATION_FORM_NAME)(state),
    currentPage: steps.length - 1 < currentPage ? 0 : currentPage,
  };
};

export const connectToVerifications = (component: any, actions: any = {}) => {
  const ConnectedComponent = connect(mapStateToProps, {
    trackAnalyticsEvent: trackAnalyticsEventAction || (() => {}),
    ...actions,
  })(component);

  const WithBreakpoint: React.FC<any> = props => {
    const isSmallOrDown = useBreakpoint('small', 'lessThanOrEqual');
    const {
      portalAuth: { loggedIn },
    } = useAuthGateState();
    const verification = useVerificationValue(value => value.verification);
    const steps = useVerificationValue(value => value.steps);
    const expired = useVerificationValue(value => value.expired);
    const submitting = useVerificationValue(value => value.submitting);
    const currentPage = useVerificationValue(value => value.currentPage);
    const { setCurrentPage } = useVerificationActions();
    const goToPage = React.useCallback(
      (page: number) => {
        setCurrentPage(page);
        window.requestAnimationFrame(() => window.scrollTo(0, 0));
      },
      [setCurrentPage],
    );
    const handleNextPage = React.useCallback(
      (page: number) => goToPage(page + 1),
      [goToPage],
    );
    const handlePreviousPage = React.useCallback(
      (page: number) => goToPage(page - 1),
      [goToPage],
    );
    const routeVerificationId = normalizeVerificationId(
      useParams().verificationId,
    );
    // Status mounts this only once the slice has a verification, but the
    // context value can lag a render behind the slice (INC-12088). It also
    // still holds the previous verification for a render after the URL moves
    // to another one. Render nothing until it is the verification of this
    // page; the context update re-renders this.
    if (!verification) return null;
    if (routeVerificationId && verification.id !== routeVerificationId) {
      return null;
    }
    return (
      <ConnectedComponent
        {...props}
        isSmallOrDown={isSmallOrDown}
        authLoggedIn={loggedIn}
        verification={verification}
        steps={steps}
        expired={expired}
        submitting={submitting}
        currentPage={currentPage}
        handleNextPage={handleNextPage}
        handlePreviousPage={handlePreviousPage}
      />
    );
  };

  return WithBreakpoint;
};

export default Verification;
